import type { DragLocationHistory } from '@atlaskit/pragmatic-drag-and-drop/types';

import type { PanelNames } from './Panels';
import {
  INBOX_MIN_WIDTH,
  PLANNER_MIN_WIDTH,
  type Panels,
  type PanelWidth,
  type PanelWidths,
} from './Panels';

// The default widths also happen to be the min widths
const INBOX_DEFAULT_WIDTH = INBOX_MIN_WIDTH;
const PLANNER_DEFAULT_WIDTH = PLANNER_MIN_WIDTH;

const DIVIDER_GUTTER_SIZE = 10;
const TOTAL_MARGIN = 32;

/**
 * Adjusts the panels' widths if needed, when toggling panels open, to ensure that the other
 * panels' widths are adjusted to accommodate the new panel as it comes into view.
 */
function adjustPanelWidthsForToggledPanel({
  screenWidth,
  toggledPanelName,
  panelWidths,
  panels,
}: {
  screenWidth: number | undefined;
  toggledPanelName: string;
  panelWidths: PanelWidths;
  panels: Panels;
}) {
  const width = screenWidth || window.innerWidth;

  const totalNumPanelsOpen =
    Number(panels.inbox) + Number(panels.planner) + Number(panels.board);

  const totalPanelWidths =
    (panels.inbox ? panelWidths['inbox'].width : 0) +
    (panels.planner ? panelWidths['planner'].width : 0) +
    (panels.board ? panelWidths['board'].width : 0);

  const availablePanelSpace =
    width - TOTAL_MARGIN - DIVIDER_GUTTER_SIZE * (totalNumPanelsOpen - 1);

  if (availablePanelSpace >= totalPanelWidths) {
    return panelWidths;
  }

  const adjustedPanelWidths = {
    inbox: panelWidths['inbox'],
    planner: panelWidths['planner'],
    board: panelWidths['board'],
  };

  if (panels.board) {
    //always take from board's width first, unless this'd put it below its min width, then
    //set the panels all to their max widths if their width is above their max width
    const wouldPutBoardBelowMinWidth =
      panelWidths.board.width - panelWidths[toggledPanelName].width <
      panelWidths.board.minWidth;

    if (wouldPutBoardBelowMinWidth) {
      if (panels.inbox) {
        adjustedPanelWidths.inbox.width = Math.min(
          panelWidths.inbox.maxWidth,
          panelWidths.inbox.width,
        );
      }

      if (panels.planner) {
        adjustedPanelWidths.planner.width = Math.min(
          panelWidths.planner.maxWidth,
          panelWidths.planner.width,
        );
      }

      if (panels.board) {
        adjustedPanelWidths.board.width = Math.min(
          panelWidths.board.maxWidth,
          panelWidths.board.width,
        );
      }
    } else {
      adjustedPanelWidths.board.width -= panelWidths[toggledPanelName].width;
    }
  }

  return adjustedPanelWidths;
}

/**
 * Computes the max width of a panel based on the other panels open in the screen
 * @param screenWidth - the width of the screen provided by the resize observer
 * @param panels - the current state of the panels, in shared state
 * @param panelName - the name of the panel to compute the max width for
 * @param storedPanelWidths - the widths of the panels, including min and max, as stored in local storage
 */
function computePanelMaxWidth({
  screenWidth,
  panels,
  panelName,
  storedPanelWidths,
}: {
  screenWidth: number | undefined;
  panels: Panels;
  panelName: string;
  storedPanelWidths: PanelWidths;
}) {
  //use a default if the resize observer doesn't provide width, which it sometimes does not
  const containerWidth = (screenWidth ?? window.innerWidth) - TOTAL_MARGIN;
  const twoPanelAvailableWidth = containerWidth - DIVIDER_GUTTER_SIZE;
  const bothDividersSize = DIVIDER_GUTTER_SIZE * 2;
  const threePanelAvailableWidth = containerWidth - bothDividersSize;

  if (panelName === 'inbox') {
    const inboxMinWidth = storedPanelWidths.inbox.minWidth;

    if (panels.planner && panels.board) {
      //board is always flexible because it's always rightmost at the moment, so use minWidth for its calculation
      return Math.max(
        threePanelAvailableWidth -
          (storedPanelWidths.planner.width ??
            storedPanelWidths.planner.minWidth) -
          storedPanelWidths.board.minWidth,
        inboxMinWidth,
      );
    }

    if (panels.planner || panels.board) {
      //planner and board have the same minWidth for now, let's just pick one
      return Math.max(
        twoPanelAvailableWidth - storedPanelWidths.planner.minWidth,
        inboxMinWidth,
      );
    }

    if (!panels.planner && !panels.board) {
      return containerWidth;
    }
  }

  if (panelName === 'planner') {
    const plannerMinWidth = storedPanelWidths.planner.minWidth;

    if (panels.inbox && panels.board) {
      //board is always flexible because it's always rightmost at the moment, so use minWidth for its calculation
      return Math.max(
        threePanelAvailableWidth -
          (storedPanelWidths.inbox.width ?? storedPanelWidths.inbox.minWidth) -
          storedPanelWidths.board.minWidth,
        plannerMinWidth,
      );
    }

    if (panels.inbox) {
      return Math.max(
        twoPanelAvailableWidth -
          (storedPanelWidths.inbox.width ?? storedPanelWidths.inbox.minWidth),
        plannerMinWidth,
      );
    }

    if (panels.board) {
      //board is always flexible because it's always rightmost at the moment, so use minWidth for its calculation
      return Math.max(
        twoPanelAvailableWidth - storedPanelWidths.board.minWidth,
        plannerMinWidth,
      );
    }

    if (!panels.inbox && !panels.board) {
      return containerWidth;
    }
  }

  if (panelName === 'board') {
    const boardMinWidth = storedPanelWidths.board.minWidth;

    if (panels.inbox && panels.planner) {
      return Math.max(
        threePanelAvailableWidth -
          (storedPanelWidths.inbox.width ?? storedPanelWidths.inbox.minWidth) -
          (storedPanelWidths.planner.width ??
            storedPanelWidths.planner.minWidth),
        boardMinWidth,
      );
    }

    if (panels.inbox) {
      return Math.max(
        twoPanelAvailableWidth -
          (storedPanelWidths.inbox.width ?? storedPanelWidths.inbox.minWidth),
        boardMinWidth,
      );
    }

    if (panels.planner) {
      //board is always flexible because it's always rightmost at the moment, so use minWidth for its calculation
      return Math.max(
        twoPanelAvailableWidth -
          (storedPanelWidths.planner.width ??
            storedPanelWidths.planner.minWidth),
        boardMinWidth,
      );
    }

    if (!panels.inbox && !panels.planner) {
      return containerWidth;
    }
  }

  return containerWidth;
}

/**
 * Computes the width of a single panel based on the screen width, the panel name, and the current state of the panels
 * @param screenWidth - the width of the entire screen
 * @param panelName - the name of the panel you're computing width for
 * @param panels - the panels currently open, gathered from shared state
 * @returns the width of the panel
 */
function computeSinglePanelWidths({
  screenWidth,
  panelName,
  panels,
  storedPanelWidths,
  returnDefaults,
}: {
  screenWidth: number;
  panelName: PanelNames;
  panels: Panels;
  storedPanelWidths: PanelWidths;
  returnDefaults?: boolean;
}): PanelWidth {
  const containerWidth = screenWidth - TOTAL_MARGIN;

  const panelWidth = storedPanelWidths[panelName] as PanelWidth;

  if (panelName === 'inbox') {
    if (returnDefaults || !panelWidth.width) {
      panelWidth.width = INBOX_DEFAULT_WIDTH;
    } else {
      panelWidth.width = panelWidth.width ?? INBOX_MIN_WIDTH;
    }

    panelWidth.maxWidth = computePanelMaxWidth({
      screenWidth,
      panels,
      panelName,
      storedPanelWidths,
    });
  }

  if (panelName === 'planner') {
    if (returnDefaults || !panelWidth.width) {
      panelWidth.width = PLANNER_DEFAULT_WIDTH;
    } else {
      panelWidth.width = panelWidth.width ?? PLANNER_MIN_WIDTH;
    }

    panelWidth.maxWidth = computePanelMaxWidth({
      screenWidth,
      panels,
      panelName,
      storedPanelWidths,
    });
  }

  if (panelName === 'board') {
    if (!panelWidth.width || returnDefaults) {
      const isInboxOpen = panels.inbox;
      const isPlannerOpen = panels.planner;

      if (isInboxOpen && isPlannerOpen) {
        panelWidth.width =
          containerWidth -
          DIVIDER_GUTTER_SIZE * 2 -
          INBOX_DEFAULT_WIDTH -
          PLANNER_DEFAULT_WIDTH;
      } else if (isPlannerOpen) {
        panelWidth.width =
          containerWidth - DIVIDER_GUTTER_SIZE - PLANNER_DEFAULT_WIDTH;
      } else {
        panelWidth.width = containerWidth - DIVIDER_GUTTER_SIZE;
      }
    }

    panelWidth.maxWidth = computePanelMaxWidth({
      screenWidth,
      panels,
      panelName,
      storedPanelWidths,
    });
  }

  panelWidth.width = Math.min(panelWidth.width, panelWidth.maxWidth);
  return panelWidth;
}

/**
 * Computes the width of the inbox, planner, and board panels
 * based on the full size of the split screen container and the
 * current state of the panels
 * @param fullSplitScreenContainerSize - The width of the split screen container
 * @param panels - The current state of the panels. Need to use this to force a re-render in the Manager component.
 * @param storedPanelWidths - The widths of the panels currently, as stored in local storage
 * @returns The width of the inbox, planner, and board panels
 */
function computePanelWidths({
  screenWidth,
  panels,
  storedPanelWidths,
  returnDefaults,
}: {
  screenWidth: number | undefined;
  panels: Panels;
  storedPanelWidths: PanelWidths;
  returnDefaults?: boolean;
}): PanelWidths {
  //use a backup if we don't have screen width from the resize observer, which we sometimes do not
  const width = screenWidth || window.innerWidth;

  return {
    inbox: computeSinglePanelWidths({
      screenWidth: width,
      panelName: 'inbox',
      panels,
      storedPanelWidths,
      returnDefaults,
    }),
    planner: computeSinglePanelWidths({
      screenWidth: width,
      panelName: 'planner',
      panels,
      storedPanelWidths,
      returnDefaults,
    }),
    board: computeSinglePanelWidths({
      screenWidth: width,
      panelName: 'board',
      panels,
      storedPanelWidths,
      returnDefaults,
    }),
  };
}

/**
 * Computes the proposed new width of the panel based on the drag & drop divider's starting and current location,
 * as well as the stored width of the panel when the divider was last dropped.
 * @param location - the location information of the drag & drop divider
 * @param maxWidth - the maximum width of the panel
 * @param minWidth - the minimum width of the panel
 * @param previousWidth - the width of the panel when the divider was last dropped
 * @returns the proposed width of the panel. If the proposed width is less than half of the minimum width, it will return 0, indicating that the panel should be collapsed.
 */
function getProposedPanelWidth({
  location,
  maxWidth,
  minWidth,
  previousWidth,
}: {
  location: DragLocationHistory;
  maxWidth: number;
  minWidth: number;
  previousWidth: number;
}): number {
  const diffX = location.current.input.clientX - location.initial.input.clientX;
  const proposedWidth = previousWidth + diffX;

  if (proposedWidth < minWidth / 2) {
    return 0;
  }

  // Ensure we don't go below the min or above the max allowed widths
  return Math.min(Math.max(minWidth, proposedWidth), maxWidth);
}

export {
  adjustPanelWidthsForToggledPanel,
  computePanelMaxWidth,
  computePanelWidths,
  computeSinglePanelWidths,
  getProposedPanelWidth,
};
